import { useState, useRef, useEffect } from "react";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useMutation } from "@tanstack/react-query";
import apiClient from "../../services/apiService";
import { getErrorMessage } from "../../utils/getErrorMessage";

const ResetPassword = () => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(58);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const otpRefs = useRef([]);

  useEffect(() => {
    let interval;
    if (step === 2 && timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [step, timer]);

  // --- API MUTATIONS ---
  const requestOtpMutation = useMutation({
    mutationFn: (payload) =>
      apiClient.post("/api/auth/reset-password-otp", payload),
    onSuccess: () => {
      toast.success("OTP sent to your email");
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, "Failed to send OTP."));
    },
  });

  const verifyOtpMutation = useMutation({
    mutationFn: (payload) =>
      apiClient.post("/api/auth/verify-reset-password-otp", payload),
    onSuccess: () => {
      toast.success("OTP Verified");
      setStep(3);
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, "Invalid OTP."));
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (payload) =>
      apiClient.post("/api/auth/reset-password", payload),
    onSuccess: () => {
      toast.success("Password reset successfully!");
      setTimeout(() => navigate("/login"), 1500);
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, "Failed to reset password."));
    },
  });

  const handleOtpChange = (value, index) => {
    if (isNaN(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);
    if (value && index < 5) otpRefs.current[index + 1].focus();
  };

  const handlePaste = (e) => {
    const data = e.clipboardData.getData("text").trim();
    if (!/^\d{6}$/.test(data)) return; // Ensure it's exactly 6 digits

    const digits = data.split("");
    setOtp(digits);
    otpRefs.current[5].focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1].focus();
    }
  };

  const validateEmail = (emailValue) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue);

  const handleSendOtp = () => {
    if (!email.trim() || !validateEmail(email)) {
      setErrors({ email: "Please enter a valid email address" });
      return;
    }
    setErrors({});
    requestOtpMutation.mutate({ type: "admin", email });
  };

  const handleVerifyEmail = () => {
    verifyOtpMutation.mutate({ type: "admin", email, otp: otp.join("") });
  };

  const handleSavePassword = () => {
    const newErrors = {};
    if (newPassword.length < 8)
      newErrors.newPassword = "Password must be at least 8 characters";
    if (newPassword !== confirmPassword)
      newErrors.confirmPassword = "Passwords do not match";
    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) {
      resetPasswordMutation.mutate({
        type: "admin",
        email,
        otp: otp.join(""),
        password: newPassword,
        password_confirmation: confirmPassword,
      });
    }
  };

  const Logo = () => (
    <div className="flex flex-col items-center mb-6">
      <span className="text-2xl font-serif italic text-[#C5A28E] leading-none">
        Trulixx
      </span>
      <span className="text-[10px] tracking-[0.2em] font-medium text-black uppercase">
        Fashion
      </span>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-[32px] shadow-sm p-10 py-12 text-center">
        <Logo />

        {step === 1 && (
          <div className="space-y-6">
            <div className="space-y-2">
              <h2 className="text-2xl font-semibold text-gray-900">
                Reset Password
              </h2>
              <p className="text-[#9397A1] text-base px-6">
                Enter your email address and we'll send you a reset code
              </p>
            </div>
            <div className="text-left space-y-2">
              <label className="text-sm font-medium text-gray-700 ml-1">
                Email
              </label>
              <input
                type="email"
                value={email}
                placeholder="Admin@trulixxfashion.com"
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) setErrors({ ...errors, email: "" });
                }}
                className={`w-full px-4 py-3 rounded-xl border ${errors.email ? "border-red-500" : "border-gray-200"} focus:outline-none focus:ring-1 focus:ring-[#DBCBB9] text-gray-600`}
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>
            <button
              onClick={handleSendOtp}
              disabled={requestOtpMutation.isPending}
              className="w-full bg-[#E2D1C3] hover:bg-[#d4c1b2] text-gray-800 font-medium py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              {requestOtpMutation.isPending ? (
                <Loader2 className="animate-spin" size={18} />
              ) : (
                "Send OTP"
              )}
            </button>
            <button
              onClick={() => navigate("/login")}
              className="text-green-700 text-sm font-medium hover:underline block w-full"
            >
              Back to Login page
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <div className="space-y-2">
              <h2 className="text-2xl font-semibold text-gray-900">
                Verify Email
              </h2>
              <p className="text-[#9397A1] text-base px-4">
                We've sent an OTP to:{" "}
                <span className="text-black font-medium">{email}</span>
              </p>
            </div>
            <div className="flex justify-between gap-2 py-4">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (otpRefs.current[index] = el)}
                  type="text"
                  maxLength={1}
                  value={digit}
                  onPaste={index === 0 ? handlePaste : undefined} // Add paste handler to first box
                  disabled={verifyOtpMutation.isPending}
                  onChange={(e) => {
                    handleOtpChange(e.target.value, index);
                    if (errors.otp) setErrors({ ...errors, otp: "" });
                  }}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  className="w-12 h-12 text-center text-lg font-medium bg-gray-100 rounded-xl focus:bg-white focus:ring-2 focus:ring-[#DBCBB9] outline-none transition-all"
                />
              ))}
            </div>
            <button
              onClick={handleVerifyEmail}
              disabled={
                otp.join("").length !== 6 || verifyOtpMutation.isPending
              }
              className={`w-full text-gray-800 font-medium py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2 ${otp.join("").length === 6 && !verifyOtpMutation.isPending ? "bg-[#E2D1C3] hover:bg-[#d4c1b2]" : "bg-gray-200 opacity-50"}`}
            >
              {verifyOtpMutation.isPending ? (
                <Loader2 className="animate-spin" size={18} />
              ) : (
                "Verify Email"
              )}
            </button>
            <div className="text-sm text-gray-400">
              {timer > 0 ? (
                <p>
                  Resend code in{" "}
                  <span className="text-[#556B2F] font-medium">
                    {timer} secs
                  </span>
                </p>
              ) : (
                <p>
                  Didn't receive the code?{" "}
                  <button
                    onClick={() =>
                      requestOtpMutation.mutate({ type: "admin", email })
                    }
                    className="text-[#556B2F] font-medium hover:underline"
                  >
                    Resend code
                  </button>
                </p>
              )}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <div className="space-y-2 text-center">
              <h2 className="text-2xl font-semibold text-gray-900">
                Create New Password
              </h2>
              <p className="text-[#9397A1] text-base px-6">
                Let's secure your account with a strong password.
              </p>
            </div>
            <div className="text-left space-y-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-[#344054] ml-1">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl border ${errors.newPassword ? "border-red-500" : "border-gray-200"} focus:outline-none focus:ring-1 focus:ring-[#DBCBB9]`}
                  />
                  <button
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-[#344054] ml-1">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl border ${errors.confirmPassword ? "border-red-500" : "border-gray-200"} focus:outline-none focus:ring-1 focus:ring-[#DBCBB9]`}
                  />
                  <button
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={20} />
                    ) : (
                      <Eye size={20} />
                    )}
                  </button>
                </div>
              </div>
            </div>
            <button
              onClick={handleSavePassword}
              disabled={resetPasswordMutation.isPending}
              className="w-full bg-[#E2D1C3] hover:bg-[#d4c1b2] text-gray-800 font-medium py-3.5 rounded-xl flex items-center justify-center gap-2"
            >
              {resetPasswordMutation.isPending ? (
                <Loader2 className="animate-spin" size={18} />
              ) : (
                "Save Password"
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
